/**
 * Dauerhafter Speicher.
 *
 * `navigator.storage.persist()` ist die wirkungsvollste einzelne Zeile der App:
 * ohne sie darf der Browser die IndexedDB bei Platzmangel loeschen, und Safari
 * raeumt nicht installierte Seiten nach etwa sieben Tagen Inaktivitaet ab.
 *
 * Chrome gewaehrt sie bei ausreichend genutzten Seiten stillschweigend,
 * Firefox fragt nach. Ein `false` ist kein Fehler, sondern eine Information
 * fuer die Einstellungen.
 */
export interface StorageStatus {
  supported: boolean;
  persistent: boolean;
  usageBytes?: number;
  quotaBytes?: number;
}

export async function getStorageStatus(): Promise<StorageStatus> {
  if (typeof navigator === 'undefined' || !navigator.storage?.estimate) {
    return { supported: false, persistent: false };
  }
  try {
    const [persistent, estimate] = await Promise.all([
      navigator.storage.persisted?.() ?? Promise.resolve(false),
      navigator.storage.estimate(),
    ]);
    return {
      supported: true,
      persistent,
      usageBytes: estimate.usage,
      quotaBytes: estimate.quota,
    };
  } catch {
    return { supported: false, persistent: false };
  }
}

export async function requestPersistentStorage(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !navigator.storage?.persist) return false;
  try {
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}

/**
 * Pruefung beim Start (F-37): einmal dauerhaften Speicher anfragen, danach
 * den Stand lesen. Das Ergebnis wird fuer die ganze Sitzung gemerkt - die
 * Uebersicht fragt so nicht vor der Anfrage nach und nicht bei jedem Besuch.
 */
let startPruefung: Promise<StorageStatus> | null = null;

export function speicherBeimStart(): Promise<StorageStatus> {
  startPruefung ??= requestPersistentStorage().then(() => getStorageStatus());
  return startPruefung;
}

/** Erneut anfragen (Knopf in den Einstellungen) - die Uebersicht sieht danach den neuen Stand. */
export function speicherErneutPruefen(): Promise<StorageStatus> {
  startPruefung = requestPersistentStorage().then(() => getStorageStatus());
  return startPruefung;
}

/** Nur fuer Tests: die gemerkte Startpruefung vergessen. */
export function speicherPruefungZuruecksetzen(): void {
  startPruefung = null;
}

export function formatBytes(bytes: number | undefined): string {
  if (bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['kB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[unit]}`;
}
